import { computed, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ROUTE_PATHS } from '../routing/route-paths';
import { ToastService } from '../services/toast.service';
import { AuthTokenStorage } from './auth-token.storage';
import { AuthSessionStore } from './auth-session.store';
import { AuthApi } from '../../features/auth/infrastructure/auth.api';
import type { AuthToken, User } from './auth.model';

@Injectable({ providedIn: 'root' })
export class AuthSessionFacade {
  private readonly store = inject(AuthSessionStore);
  private readonly tokenStorage = inject(AuthTokenStorage);
  private readonly router = inject(Router);
  private readonly toastService = inject(ToastService);
  private readonly authApi = inject(AuthApi);
  private loggingOut = false;

  readonly user = this.store.user;
  readonly token = this.store.token;
  readonly isLoading = this.store.isLoading;
  readonly error = this.store.error;
  readonly isAuthenticated = this.store.isAuthenticated;
  readonly isSuperAdmin = computed(() => this.user()?.userType === 'SUPER_ADMIN');

  setLoading(isLoading: boolean): void {
    this.store.setLoading(isLoading);
  }

  initiateLogin(): void {
    void this.authApi.initiateLogin();
  }

  async handleCallback(code: string, state: string): Promise<void> {
    this.store.setLoading(true);
    this.store.setError(null);

    try {
      const token = await this.authApi.exchangeCode(code, state);
      const user = await this.authApi.getUserInfo(token.accessToken);

      this.setSession(user, token);
      this.toastService.success('Sign in success', 'Welcome back to IAM Platform.');
      await this.router.navigate([this.getDefaultRouteForUser(user)]);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Authentication failed';
      this.store.setError(message);
      this.toastService.error('Sign in failed', message);
      await this.router.navigate([ROUTE_PATHS.LOGIN]);
    } finally {
      this.store.setLoading(false);
    }
  }

  async refreshSession(): Promise<boolean> {
    const currentRefreshToken = this.tokenStorage.getRefreshToken();
    if (!currentRefreshToken) return false;

    try {
      const token = await this.authApi.refreshToken(currentRefreshToken);
      const user = await this.authApi.getUserInfo(token.accessToken);
      this.setSession(user, token);
      return true;
    } catch {
      void this.logout();
      return false;
    }
  }

  restoreSession(): void {
    const token = this.tokenStorage.getToken();
    if (!token) return;

    if (this.tokenStorage.isTokenExpired(token)) {
      void this.refreshSession();
      return;
    }

    const storedUser = localStorage.getItem('iam_auth_user');
    if (!storedUser) {
      void this.refreshSession();
      return;
    }

    try {
      const user: User = JSON.parse(storedUser);
      this.store.restoreSession(user, token);
    } catch {
      this.tokenStorage.clearAll();
    }
  }

  setSession(user: User, token: AuthToken): void {
    this.store.setUser(user);
    this.store.setToken(token);
    this.tokenStorage.setToken(token);
    localStorage.setItem('iam_auth_user', JSON.stringify(user));
  }

  async logout(): Promise<void> {
    if (this.loggingOut) return;
    this.loggingOut = true;

    try {
      // Call backend to revoke token + session
      await this.authApi.logout();
    } finally {
      // Clear local state
      this.store.logout();
      this.tokenStorage.clearAll();
      localStorage.removeItem('iam_auth_user');
      this.toastService.info('Signed out', 'Your session has been ended.');
      void this.router.navigate([ROUTE_PATHS.LOGIN]);
      this.loggingOut = false;
    }
  }

  setError(message: string): void {
    this.store.setError(message);
  }

  clearError(): void {
    this.store.setError(null);
  }

  getDefaultRoute(): string {
    return this.getDefaultRouteForUser(this.user());
  }

  getDefaultRouteForUser(_user: Pick<User, 'userType'> | null | undefined): string {
    return ROUTE_PATHS.QUICK_ACCESS;
  }
}
